import { createStore } from "jotai";
import { atom } from "jotai";
import { PaymentOption } from "./PaymentOptionRadioGroup";
import type { RefObject } from "react";
import type { IGetBookingCustomerQueryResult } from "@repo/rq-api-public";
import type { CustomerCardRef } from "./CustomerCard";

export const jotaiStore = createStore();

export const bookingGuidAtom = atom<string | null>(null);
export const multibookingGuidAtom = atom<string | null>(null);
export const isAuthenticatedAtom = atom<boolean>(false);
export const isAnonymousSessionAtom = atom<boolean>(false);
export const paymentOptionAtom = atom(PaymentOption.Full);
export const bookingCultureAtom = atom<string>("en-US");
export const bookingCurrencyAtom = atom<string>("USD");

// null = server-filled (value unknown; send null on save = no-op)
// ""   = user explicitly cleared (send "" on save = clear field)
// string = known value
export type FieldValue = string | null;

/**
 * Snapshot of the customer details an anonymous session has saved on the current booking.
 * The GET /bookings/{guid}/customer endpoint 403s for anonymous sessions, so this is the only
 * place the UI can learn what data is on the booking. Written by CustomerCard after a successful
 * save; read by BookingDetail to gate the Pay button via `computeMissingFields` and by
 * CustomerCard's display mode so saved values show up in the card.
 */
export type AnonymousSavedCustomer = Pick<
  IGetBookingCustomerQueryResult,
  | "fullName"
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "idPassportNumber"
  | "address"
  | "billingInfo"
>;

export const anonymousSavedCustomerAtom = atom<AnonymousSavedCustomer | null>(
  null,
);

/**
 * Bridges the Customer card to OrderSummary's BillingDetail (a sibling, not a
 * descendant) so "Add your details" can open/save the card's form: `ref` is
 * the card's ref *object*, `isEditing` mirrors its edit-mode state so the
 * button can switch label to "Save", and `isSaving` lets it mirror the same
 * disabled/loading state while the save request is in flight.
 *
 * Holds the RefObject, never `ref.current` — the card rebuilds its handle each
 * render, so a stored handle would save that render's stale form values.
 */
export interface CustomerCardState {
  ref: RefObject<CustomerCardRef | null> | null;
  isEditing: boolean;
  isSaving: boolean;
}

export const customerCardStateAtom = atom<CustomerCardState>({
  ref: null,
  isEditing: false,
  isSaving: false,
});
